import EventBannerItem, {
  EventBannerItemFragment,
} from '-/Event/EventBanner/EventBannerItem';
import { View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Box from '@/components/UI/Box';
import { type FragmentOf, graphql, readFragment } from '@/gql/graphql';
import useRerenderMinutely from '@/hooks/useRerenderMinutely';
import EventBannerBackground, {
  EventBannerBackgroundFragment,
} from './EventBannerBackground';
import {
  EventBannerScoreEventTeamFragment,
  EventBannerScoreFragment,
} from './EventBannerScore';

export const EventBannerFragment = graphql(/* GraphQL */ `
  fragment EventBanner on event {
    ...EventBannerScore
    eventTeams: event_teams(
      limit: 2
      order_by: [{ is_home: asc }, { team: { title: asc } }]
    ) {
      event_id
      team_id
      ...EventBannerItem
      ...EventBannerBackground
      ...EventBannerScoreEventTeam
    }
  }
`, [
  EventBannerScoreFragment,
  EventBannerItemFragment,
  EventBannerBackgroundFragment,
  EventBannerScoreEventTeamFragment,
]);

export const BackgroundContainer = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return <View style={style.backgroundContainer}>{children}</View>;
};

export const BannerContainer = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return <View style={style.bannerContainer}>{children}</View>;
};

export const BannerItemContainer = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return <View style={style.bannerItemContainer}>{children}</View>;
};

export const BackgroundFilter = ({ opacity = 0.7 }: { opacity?: number }) => {
  return <View style={style.backgroundFilter(opacity)} />;
};

function EventBanner({
  fragment,
  info,
  hideScore = false,
}: {
  fragment: FragmentOf<typeof EventBannerFragment> | null | undefined;
  info?: React.ReactNode | null;
  hideScore?: boolean;
}) {
  const event = readFragment(EventBannerFragment, fragment);
  useRerenderMinutely();
  const [awayTeam, homeTeam] = event?.eventTeams ?? [];

  if (!awayTeam || !homeTeam) {
    return null;
  }

  return (
    <Box style={style.container} height={160}>
      <BackgroundContainer>
        <EventBannerBackground fragment={awayTeam} />
        <EventBannerBackground fragment={homeTeam} />
      </BackgroundContainer>
      <BackgroundFilter />
      <BannerItemContainer>
        <EventBannerItem fragment={awayTeam} index={0} hideScore={hideScore} />
        <EventBannerItem fragment={homeTeam} index={1} hideScore={hideScore} />
      </BannerItemContainer>
      {info}
    </Box>
  );
}

const style = StyleSheet.create({
  container: {
    flexDirection: 'row',
  },
  backgroundContainer: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    flex: 1,
  },
  bannerContainer: {
    flex: 1,
    flexDirection: 'row',
  },
  bannerItemContainer: {
    flex: 1,
    flexDirection: 'column',
  },
  backgroundFilter: (opacity: number) => ({
    backgroundColor: 'black',
    width: '100%',
    height: '100%',
    position: 'absolute',
    opacity,
  }),
});

export default EventBanner;
